import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Navbar from '../../components/Navbar'
import PageHeader from '../../components/PageHeader'
import { CATEGORIES, getArticlesByCategory, getArticleBySlug } from './articles'

// ─── SupportLayout ───────────────────────────────────────────────────────────
//
// Two-column layout shared by every support page:
//   - Left: collapsible category sidebar listing every article (desktop only)
//   - Right: the page content (passed as children)
//
// On mobile, the sidebar moves above the content as a collapsible
// "Browse all articles" disclosure. The current article (if any) is
// highlighted so the user always knows where they are in the hierarchy.
//
// Props:
//   currentSlug: slug of the article currently open, for highlight (optional)
//   back: { label, to } passed through to PageHeader (optional)
//   title: page H1 / PageHeader title
//   subtitle: optional subtitle for PageHeader
//   children: main content

export default function SupportLayout({ currentSlug, back, title, subtitle, children }) {
  const { pathname } = useLocation()

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)' }}>
      <Navbar />
      <PageHeader
        back={back}
        title={title}
        subtitle={subtitle}
      />
      <div className="container" style={{ padding: '0 24px 64px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gap: 32,
        }}
        className="support-grid"
        >
          {/* Category sidebar */}
          <Sidebar currentSlug={currentSlug} pathname={pathname} />

          {/* Main content */}
          <main style={{ minWidth: 0 }}>
            {children}
          </main>
        </div>
      </div>

      {/* The grid switches to 2-column at the same breakpoint we use
          everywhere else (720px from the FAQ work). */}
      <style>{`
        @media (min-width: 860px) {
          .support-grid {
            grid-template-columns: 240px minmax(0, 1fr) !important;
          }
        }
        /* Hover state for non-current sidebar links. Subtle background tint
           that emphasises which item the cursor is on. The current article
           keeps its own accent-light background and isn't affected. */
        .support-sidebar-link[data-current="false"]:hover {
          background: var(--surface) !important;
        }
      `}</style>
    </div>
  )
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────
//
// Each category is collapsible. The default open state is:
//
//   - On an article page: only the category the article belongs to is open.
//     Everything else is collapsed.
//   - On the support home or contact page: all categories are open (since
//     there's no "current" article to anchor on, hiding everything would
//     make the sidebar feel broken).
//
// Once the user toggles a category, that choice is remembered for the
// session via local state. Toggling another category open doesn't close
// the one they're currently in.

function Sidebar({ currentSlug, pathname }) {
  // Work out which category is "current" based on the article slug.
  // null when there isn't one (home / contact page).
  const currentCategoryId = currentSlug
    ? (getArticleBySlug(currentSlug)?.category ?? null)
    : null

  // Initial open state: object keyed by category id, boolean value.
  // When there's a current article, only that category starts open.
  // Otherwise, all categories start open.
  const [openMap, setOpenMap] = useState(() => {
    const initial = {}
    CATEGORIES.forEach(cat => {
      initial[cat.id] = currentCategoryId
        ? cat.id === currentCategoryId
        : true
    })
    return initial
  })

  // SupportLayout stays mounted as the user navigates between articles, so
  // state would otherwise carry over (and the wrong category would stay
  // open). Re-derive the default whenever the current category changes.
  // User toggles within a given article are preserved; switching articles
  // resets to the "only the new category is open" default.
  useEffect(() => {
    const next = {}
    CATEGORIES.forEach(cat => {
      next[cat.id] = currentCategoryId
        ? cat.id === currentCategoryId
        : true
    })
    setOpenMap(next)
  }, [currentCategoryId])

  const toggleCategory = (catId) => {
    setOpenMap(m => ({ ...m, [catId]: !m[catId] }))
  }

  return (
    <aside style={{
      fontSize: '0.92rem',
      background: 'var(--white)',
      border: '1px solid var(--surface-2)',
      borderRadius: 10,
      padding: '20px 18px',
      height: 'fit-content',
      position: 'sticky',
      top: 80,
    }}>
      <Link
        to="/support"
        style={{
          display: 'block',
          fontSize: '0.85rem',
          fontWeight: 600,
          color: pathname === '/support' ? 'var(--accent)' : 'var(--ink)',
          textDecoration: 'none',
          marginBottom: 16,
          padding: '4px 0',
        }}
      >
        ← Support home
      </Link>

      {CATEGORIES.map(cat => {
        const articles = getArticlesByCategory(cat.id)
        const isOpen   = openMap[cat.id]
        return (
          <div key={cat.id} style={{ marginBottom: 6 }}>
            <button
              onClick={() => toggleCategory(cat.id)}
              aria-expanded={isOpen}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                background: 'none',
                border: 'none',
                padding: '8px 4px',
                fontSize: '0.72rem',
                fontWeight: 700,
                color: 'var(--ink-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                cursor: 'pointer',
                fontFamily: 'inherit',
                textAlign: 'left',
              }}
            >
              <span>{cat.label}</span>
              <span
                aria-hidden="true"
                style={{
                  fontSize: '0.85rem',
                  color: 'var(--ink-faint)',
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s',
                  lineHeight: 1,
                }}
              >
                ⌄
              </span>
            </button>

            {isOpen && (
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: '4px 0 14px',
                /* Hairline divider above the first item; subsequent dividers
                   come from each li's border-top below. This visually breaks
                   the items apart so multi-word titles don't blur into one
                   paragraph. */
                borderTop: '1px solid var(--surface-2)',
              }}>
                {articles.map((article, idx) => {
                  const isCurrent = article.slug === currentSlug
                  return (
                    <li
                      key={article.slug}
                      style={{
                        /* Divider between items (top border on all except
                           the first, which uses the ul's borderTop). */
                        borderTop: idx === 0 ? 'none' : '1px solid var(--surface-2)',
                      }}
                    >
                      <Link
                        to={`/support/${article.slug}`}
                        className="support-sidebar-link"
                        data-current={isCurrent ? 'true' : 'false'}
                        style={{
                          display: 'block',
                          padding: '9px 10px',
                          textDecoration: 'none',
                          fontSize: '0.86rem',
                          lineHeight: 1.4,
                          color: isCurrent ? 'var(--accent)' : 'var(--ink)',
                          fontWeight: isCurrent ? 600 : 400,
                          background: isCurrent ? 'var(--accent-light)' : 'transparent',
                          borderLeft: isCurrent ? '3px solid var(--accent)' : '3px solid transparent',
                          transition: 'background 0.12s',
                        }}
                      >
                        {article.title}
                        {article.draft && (
                          <span style={{
                            fontSize: '0.68rem',
                            marginLeft: 6,
                            color: 'var(--ink-faint)',
                            fontWeight: 400,
                          }}>
                            (soon)
                          </span>
                        )}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )
      })}

      <hr style={{ border: 'none', borderTop: '1px solid var(--surface-2)', margin: '16px 0' }} />

      <Link
        to="/support/contact"
        style={{
          display: 'block',
          padding: '8px 10px',
          borderRadius: 6,
          background: pathname === '/support/contact' ? 'var(--accent-light)' : 'var(--surface)',
          color: pathname === '/support/contact' ? 'var(--accent)' : 'var(--ink)',
          fontWeight: 600,
          fontSize: '0.88rem',
          textDecoration: 'none',
          textAlign: 'center',
          marginBottom: 8,
        }}
      >
        Contact support →
      </Link>

      <Link
        to="/support/suggest"
        style={{
          display: 'block',
          padding: '8px 10px',
          borderRadius: 6,
          background: pathname === '/support/suggest' ? 'var(--accent-light)' : 'transparent',
          color: pathname === '/support/suggest' ? 'var(--accent)' : 'var(--ink-muted)',
          fontWeight: 500,
          fontSize: '0.86rem',
          textDecoration: 'none',
          textAlign: 'center',
          border: '1px solid var(--surface-2)',
        }}
      >
        Suggest a feature
      </Link>
    </aside>
  )
}
